import { useState, useCallback, useEffect, useRef, useContext } from 'react';

import { AI_MODEL } from '@vyce/core/src/types';
import { createResponseAiRequest } from '@vyce/core/src/api/ai';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';

interface Props {
  getDataForAIAnalytics: () => Promise<any>;
}

export const useFormAIAnalytics = ({ getDataForAIAnalytics }: Props) => {
  const [loading, setLoading] = useState(false);
  const [aiAnalytics, setAiAnalytics] = useState<string>('');
  const [progress, setProgress] = useState(0);
  const [focusOfAnalysis, setFocusOfAnalysis] = useState<string>('');
  const { showNotification } = useContext(NotificationContext);
  const [dataLoading, setDataLoading] = useState(false);
  const markdownRef = useRef<HTMLDivElement>(null);
  const [openAIAnalyticsDialog, setOpenAIAnalyticsDialog] = useState(false);

  const getAIAnalytics = useCallback(
    async (focusOfAnalysisValue: string) => {
      const trimmedFocus = focusOfAnalysisValue.trim();
      try {
        setLoading(true);
        setProgress(20);

        setDataLoading(true);
        const data = await getDataForAIAnalytics();
        setDataLoading(false);
        setProgress(40);

        const promptContent = `This data summarises the status of the qualifications of my team members. Use the JSON provided in {FORM_DATA} (already filtered) as the source of truth. Typical shape for each item:
- certificate_id: uuid
- user_id: uuid
- first_name, last_name: strings
- gender: male|female|other|null
- photo: url|null
- title: qualification title
- expiry_date: ISO date|null
- status: Valid|Expiring|Expired

Filters already applied in the dataset:
1) Teams – either all teams or a selection of teams.
2) Qualifications – all, one, or multiple qualifications.
3) Status – Valid, Expiring, or Expired for the chosen qualifications.

Here is the table to analyse:
${JSON.stringify(data, null, 2)}

Your task:
- Analyse the table and highlight key insights by team/qualification/status.
- Call out trends: what’s valid, what’s expiring soon, what’s expired; any clusters by team or qualification.
- Provide succinct, actionable recommendations or next steps (e.g., renewals to prioritise, reminders, training needs).
- Keep it brief, clear, and engaging. You may use words or simple ASCII graphs if helpful.
- End by inviting the administrator to ask if they want to delve deeper into anything.
- Add emojis to make the analysis more engaging and easy to understand, but keep official tone and language.
- Add additional block to answer this question based on the provided data in the table: ${trimmedFocus}.`;

        setProgress(60);

        const res = await createResponseAiRequest({
          input: [{ role: 'assistant', content: promptContent }],
          model: AI_MODEL.GPT_4O, // Using GPT-4o with 128k token limit (maximum for OpenAI models)
          tools: [],
        });

        setProgress(90);
        const resContent = res.data.body?.output_text || '';
        setAiAnalytics(resContent);
        setProgress(100);
        setLoading(false);
      } catch (error) {
        console.error(error);
        setProgress(0);
      }
    },
    [getDataForAIAnalytics]
  );

  const regenerateAIAnalytics = () => {
    setAiAnalytics('');
    setProgress(0);
    getAIAnalytics(focusOfAnalysis);
  };

  const copyToClipboard = async () => {
    if (!aiAnalytics || !markdownRef.current) {
      return;
    }
    const htmlContent = markdownRef.current.innerHTML;
    const textContent = markdownRef.current.innerText || markdownRef.current.textContent || '';

    // Copy both HTML (for formatted paste) and plain text (for fallback)
    const clipboardItem = new ClipboardItem({
      'text/html': new Blob([htmlContent], { type: 'text/html' }),
      'text/plain': new Blob([textContent], { type: 'text/plain' }),
    });

    try {
      await navigator.clipboard.write([clipboardItem]);
    } catch (err) {
      // Fallback to plain text if ClipboardItem is not supported
      await navigator.clipboard.writeText(textContent);
    }

    showNotification({
      message: 'AI analysis copied to clipboard',
      options: {
        variant: 'success',
      },
    });
  };

  const handleClose = () => {
    setOpenAIAnalyticsDialog(false);
    setAiAnalytics('');
    setProgress(0);
    setFocusOfAnalysis('');
  };

  useEffect(() => {
    if (!openAIAnalyticsDialog) {
      return;
    }

    setProgress(50);
    getAIAnalytics('');
  }, [openAIAnalyticsDialog]);

  return {
    loading,
    aiAnalytics,
    progress,
    setProgress,
    focusOfAnalysis,
    setFocusOfAnalysis,
    dataLoading,
    markdownRef,
    regenerateAIAnalytics,
    copyToClipboard,
    handleClose,
    openAIAnalyticsDialog,
    setOpenAIAnalyticsDialog,
  };
};
